import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import type { Editor } from "@tiptap/react";
import { v4 as uuidv4 } from "uuid";
import { cx } from "shared/utils";
import { Avatar } from "shared/components/Avatar";
import type {
  MentionHandler,
  TMentionSection,
  MentionSuggestion
} from "./types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { Show } from "shared/components/helpers/Show";

export type MentionsListDropdownProps = {
  command: (item: MentionSuggestion) => void;
  query: string;
  editor: Editor;
} & Pick<MentionHandler, "searchCallback">;

export const MentionsListDropdown = forwardRef(
  (props: MentionsListDropdownProps, ref) => {
    const { command, query, searchCallback } = props;

    const [sections, setSections] = useState<TMentionSection[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const commandListContainer = useRef<HTMLDivElement | null>(null);

    const allItems = sections.flatMap((section, sectionIndex) =>
      section.items.map((item, itemIndex) => ({
        ...item,
        sectionIndex,
        itemIndex
      }))
    );

    const selectItem = (index: number) => {
      const item = allItems[index];

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- it's necessary, sure
      if (item && !item.disabled) {
        const transactionId = uuidv4();
        command({
          ...item,
          id: transactionId
        });
      }
    };

    const upHandler = () => {
      let newIndex = (selectedIndex + allItems.length - 1) % allItems.length;

      while (allItems[newIndex]?.disabled && newIndex !== selectedIndex)
        newIndex = (newIndex + allItems.length - 1) % allItems.length;

      setSelectedIndex(newIndex);
    };

    const downHandler = () => {
      let newIndex = (selectedIndex + 1) % allItems.length;

      while (allItems[newIndex]?.disabled && newIndex !== selectedIndex)
        newIndex = (newIndex + 1) % allItems.length;

      setSelectedIndex(newIndex);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === "ArrowUp") {
          upHandler();
          return true;
        }

        if (event.key === "ArrowDown") {
          downHandler();
          return true;
        }

        if (event.key === "Enter") {
          enterHandler();
          return true;
        }

        return false;
      }
    }));

    useEffect(() => {
      const firstEnabledIndex = allItems.findIndex((item) => !item.disabled);
      setSelectedIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0);
    }, [sections]);

    useEffect(() => {
      const fetchSuggestions = async () => {
        setIsLoading(true);
        try {
          const sectionsResponse = await searchCallback?.(query);
          if (sectionsResponse) setSections(sectionsResponse);
        } catch (error) {
          console.error("Failed to fetch suggestions:", error);
        } finally {
          setIsLoading(false);
        }
      };
      void fetchSuggestions();
    }, [query, searchCallback]);

    // Scroll to selected item
    useEffect(() => {
      const container = commandListContainer.current;
      if (!container) return;

      const selectedItem = allItems[selectedIndex];
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- it's necessary, sure
      if (selectedItem) {
        const item = container.querySelector(
          `#mention-item-${selectedItem.sectionIndex}-${selectedItem.itemIndex}`
        );
        if (item) item.scrollIntoView({ block: "nearest" });
      }
    }, [selectedIndex, allItems]);

    if (isLoading)
      return (
        <div
          ref={commandListContainer}
          className="relative z-[150] max-h-[240px] w-[14rem] overflow-y-auto rounded-md border-[0.5px] px-4 py-3.5 space-y-2 bg-white"
        >
          <div className="text-center text-sm text-content-400">Loading...</div>
        </div>
      );

    return (
      <div
        ref={commandListContainer}
        className="relative z-[150] max-h-[240px] w-[14rem] overflow-y-auto rounded-lg border-[0.5px] space-y-2 bg-white"
      >
        {sections.length ? (
          sections.map((section, sectionIndex) => (
            <div key={section.key} className="divide-y divide-alpha-100">
              {section.items.length > 0 ? (
                section.items.map((item, itemIndex) => {
                  const flatIndex = allItems.findIndex(
                    (flatItem) =>
                      flatItem.sectionIndex === sectionIndex &&
                      flatItem.itemIndex === itemIndex
                  );
                  const isSelected = flatIndex === selectedIndex;

                  return (
                    <Tooltip key={item.id} placement="left">
                      <TooltipTrigger className="w-full">
                        <button
                          disabled={item.disabled}
                          id={`mention-item-${sectionIndex}-${itemIndex}`}
                          type="button"
                          className={cx(
                            "flex items-center gap-2 w-full px-4 py-3.5 text-left truncate text-content-900 text-sm font-medium",
                            isSelected ? "bg-primary-500 !text-white" : "",
                            item.disabled
                              ? "bg-alpha-40 cursor-not-allowed !text-content-100"
                              : ""
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            selectItem(flatIndex);
                          }}
                          onMouseEnter={() => {
                            if (!item.disabled) setSelectedIndex(flatIndex);
                          }}
                        >
                          {item.icon || (
                            <Avatar
                              name={item.title}
                              size={22}
                              className="text-[9px] font-extrabold"
                            />
                          )}
                          <p className="flex-grow truncate">{item.title}</p>
                        </button>
                      </TooltipTrigger>
                      <Show if={item.tooltip}>
                        <TooltipContent
                          style={{ zIndex: 151 }}
                          className="max-w-[370px] rounded-lg z-[50] p-4 text-sm text-content-1000-inverted"
                        >
                          {item.tooltip}
                        </TooltipContent>
                      </Show>
                    </Tooltip>
                  );
                })
              ) : (
                <p className="px-4 py-3.5 text-center text-sm text-content-400">
                  No results
                </p>
              )}
            </div>
          ))
        ) : (
          <p className="px-4 py-3.5 text-center text-sm text-content-400">
            No results
          </p>
        )}
      </div>
    );
  }
);

MentionsListDropdown.displayName = "MentionsListDropdown";
